import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { 
  ChevronRight, ChevronLeft, X, BookOpen, Wand2, Brain, 
  Stethoscope, Lightbulb, PenTool, Crown, Sparkles, Map,
  Upload, Users, Globe, Gift, Mic, Download, FileText,
  Feather, Star, Zap, Lock, CheckCircle
} from 'lucide-react';

const CAT_POINTING = "https://static.prod-images.emergentagent.com/jobs/9146f3d9-a3b8-4907-acc0-5321df3c8e86/images/ca69ec00c6f5bf536f17df3e20023fc404b0ffc70d4976d36b66a4c558024b12.png";

const TOUR_STEPS = [
  {
    id: 'welcome',
    icon: BookOpen,
    title: 'Welcome to Shweebum!',
    description: 'Your AI storytelling companion. Let me show you everything you can do — including tons of FREE features!',
    type: 'intro',
  },
  // FREE FEATURES SECTION
  {
    id: 'free-intro',
    icon: Gift,
    title: '🎁 FREE Features First!',
    description: 'Good news — you can plan your ENTIRE book for free! Here\'s what you get without paying a cent:',
    type: 'free-section',
    features: [
      'Unlimited story planning & outlining',
      'Full character creator with backstories',
      'World-building tools',
      'Story Doctor analysis (find plot holes)',
      '5 AI-written sample chapters',
    ]
  },
  {
    id: 'upload-free',
    icon: Upload,
    title: 'Upload Your Book (FREE)',
    description: 'Already have chapter outlines, character profiles, or world rules written? Upload them!\n\n✨ Your content is copied VERBATIM — we don\'t change a single word.\n\nSupports PDF, DOCX, and TXT files.',
    type: 'free',
  },
  {
    id: 'new-book',
    icon: Sparkles,
    title: 'Start Your Story (FREE)',
    description: 'Click "New Book" to begin. You have 3 FREE ways to start:\n\n• Upload Your Book – Import existing outlines\n• I Know My Story – Set up everything manually\n• I Have an Idea – Describe a concept, AI creates the outline',
    type: 'free',
  },
  {
    id: 'character-builder',
    icon: Users,
    title: 'Character Creator (FREE)',
    description: 'Build unlimited characters with full detail:\n\n• Name, age, appearance\n• Personality & motivation\n• Backstory & relationships\n• Role in your story\n\nNo limits on how many characters you create!',
    type: 'free',
  },
  {
    id: 'world-builder',
    icon: Globe,
    title: 'World Builder (FREE)',
    description: 'Define your story\'s world:\n\n• Time period & setting\n• Technology level\n• Magic/supernatural rules\n• Society & culture\n• Religion & beliefs\n\nPerfect for fantasy, sci-fi, or any genre!',
    type: 'free',
  },
  {
    id: 'story-doctor-free',
    icon: Stethoscope,
    title: 'Story Doctor (FREE)',
    description: 'Our AI analyzes your story for:\n\n• Plot holes & inconsistencies\n• Character arc issues\n• Pacing problems\n• Loose threads\n\n💡 Get a full health report for your story — completely free!',
    type: 'free',
  },
  {
    id: 'chapter-planning',
    icon: Map,
    title: 'Chapter Planning (FREE)',
    description: 'Plan every chapter of your book:\n\n• Map key story moments\n• Define chapter beats\n• Track character arcs\n• Organize plot threads\n\nPlan up to 54 chapters per book!',
    type: 'free',
  },
  // PAID FEATURES SECTION
  {
    id: 'paid-intro',
    icon: Star,
    title: '⭐ Premium Features',
    description: 'Ready to turn your outline into a full book? Here\'s what paid plans unlock:',
    type: 'paid-section',
  },
  {
    id: 'ai-writing',
    icon: Wand2,
    title: 'AI Chapter Writing',
    description: 'Turn your chapter outlines into full prose!\n\n• Premium Claude AI writes in your style\n• Respects your story bible\n• Maintains character consistency\n\n💰 Plus ($10/mo): 100K words/month\n💎 Premium ($20/mo): 300K words/month',
    type: 'paid',
    tier: 'plus',
  },
  {
    id: 'ai-voice',
    icon: Mic,
    title: 'AI Voice Reader',
    description: 'Listen to your chapters read aloud!\n\n• Catch awkward phrasing\n• Check dialogue flow\n• Great for editing\n\n💰 Plus: 10 chapters/month\n💎 Premium: Unlimited',
    type: 'paid',
    tier: 'plus',
  },
  {
    id: 'export',
    icon: Download,
    title: 'Export Your Book',
    description: 'Download your finished work:\n\n• PDF for printing/sharing\n• DOCX for editing in Word\n• TXT for plain text\n\n💰 Available on Plus & Premium plans',
    type: 'paid',
    tier: 'plus',
  },
  {
    id: 'full-book-gen',
    icon: Zap,
    title: 'Full Book Generation',
    description: 'One click = entire book written!\n\nShweebum takes your outline and writes EVERY chapter automatically.\n\n💎 Premium exclusive ($20/mo)',
    type: 'paid',
    tier: 'premium',
  },
  {
    id: 'continue-style',
    icon: Crown,
    title: 'Continue My Style',
    description: 'AI learns YOUR writing voice!\n\nUpload samples of your writing, and the AI matches your:\n• Word choices\n• Sentence rhythms\n• Unique tone\n\n💎 Premium exclusive ($20/mo)',
    type: 'paid',
    tier: 'premium',
  },
  {
    id: 'finish',
    icon: Feather,
    title: 'You\'re Ready!',
    description: 'Start with the FREE features — plan your entire book!\n\nUpgrade anytime when you\'re ready to write.\n\n💡 Tip: Click the menu (⋯) in settings to replay this tour anytime.',
    type: 'outro',
  },
];

export const GuidedTour = ({ active, onComplete, userTier = 'free' }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (active) {
      setStepIndex(0);
      setTimeout(() => setIsVisible(true), 100);
    } else {
      setIsVisible(false);
    }
  }, [active]);

  const currentStep = TOUR_STEPS[stepIndex];
  const Icon = currentStep?.icon || BookOpen;

  const handleNext = () => {
    if (stepIndex < TOUR_STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    setTimeout(() => onComplete(), 200);
  };

  // Get background color based on step type
  const getStepStyle = () => {
    switch (currentStep.type) {
      case 'free':
      case 'free-section':
        return 'bg-gradient-to-br from-emerald-700/80 to-teal-900/80';
      case 'paid':
      case 'paid-section':
        return currentStep.tier === 'premium' 
          ? 'bg-gradient-to-br from-violet-700/80 to-purple-900/80'
          : 'bg-gradient-to-br from-cyan-700/80 to-blue-900/80';
      case 'intro':
      case 'outro':
        return 'bg-gradient-to-br from-cyan-600/70 to-blue-800/80';
      default:
        return 'bg-gradient-to-br from-slate-800 to-slate-900';
    }
  };

  const getTagBadge = () => {
    switch (currentStep.type) {
      case 'free':
      case 'free-section':
        return (
          <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/30 border border-emerald-500/50 text-emerald-200 px-2 py-1 rounded-full">
            <Gift className="w-3 h-3" />
            FREE
          </span>
        );
      case 'paid':
      case 'paid-section':
        return currentStep.tier === 'premium' ? (
          <span className="inline-flex items-center gap-1 text-xs bg-violet-500/30 border border-violet-500/50 text-violet-200 px-2 py-1 rounded-full">
            <Crown className="w-3 h-3" />
            Premium $20/mo
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs bg-cyan-500/30 border border-cyan-500/30 text-cyan-200 px-2 py-1 rounded-full">
            <Star className="w-3 h-3" />
            Plus $10/mo
          </span>
        );
      default:
        return null;
    }
  };

  if (!active) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleComplete}
      />

      {/* Modal */}
      <div 
        className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'} pointer-events-none`}
      >
        <div 
          className="w-full max-w-md bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl overflow-hidden pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`p-6 text-center relative ${getStepStyle()}`}>
            <button 
              onClick={handleComplete}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              data-testid="close-tour-btn"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Cat + Icon row */}
            <div className="flex items-center justify-center gap-3 mb-3">
              <img 
                src={CAT_POINTING} 
                alt="Shweebum cat" 
                className="w-14 h-14 object-contain drop-shadow-lg"
              />
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                currentStep.type === 'free' || currentStep.type === 'free-section'
                  ? 'bg-emerald-500/30 border border-emerald-500/50'
                  : currentStep.type === 'paid' || currentStep.type === 'paid-section'
                    ? currentStep.tier === 'premium'
                      ? 'bg-violet-500/30 border border-violet-500/50'
                      : 'bg-cyan-500/30 border border-cyan-500/30'
                    : 'bg-cyan-500/30 border border-cyan-500/50'
              }`}>
                <Icon className={`w-7 h-7 ${
                  currentStep.type === 'free' || currentStep.type === 'free-section'
                    ? 'text-emerald-300'
                    : currentStep.type === 'paid' || currentStep.type === 'paid-section'
                      ? currentStep.tier === 'premium' ? 'text-violet-300' : 'text-cyan-300'
                      : 'text-cyan-300'
                }`} />
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-white mb-2">{currentStep.title}</h2>
            {getTagBadge()}
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">
              {currentStep.description}
            </p>
            
            {/* Feature list for section intros */}
            {currentStep.features && (
              <ul className="mt-4 space-y-2">
                {currentStep.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
            )}
            
            {/* Upsell for paid features when user is free */}
            {currentStep.type === 'paid' && userTier === 'free' && (
              <div className="mt-4 p-3 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg">
                <p className="text-xs text-cyan-300/90 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Upgrade to unlock this feature!
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            {/* Progress bar */}
            <div className="flex justify-center gap-1 mb-4 flex-wrap">
              {TOUR_STEPS.map((step, i) => (
                <button
                  key={i}
                  onClick={() => setStepIndex(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === stepIndex 
                      ? `w-4 ${
                          step.type === 'free' || step.type === 'free-section' ? 'bg-emerald-500' :
                          step.type === 'paid' || step.type === 'paid-section' 
                            ? step.tier === 'premium' ? 'bg-violet-500' : 'bg-cyan-500'
                            : 'bg-cyan-500'
                        }` 
                      : i < stepIndex 
                        ? 'w-1.5 bg-slate-600' 
                        : 'w-1.5 bg-slate-800'
                  }`}
                  title={step.title}
                />
              ))}
            </div>
            
            {/* Step counter */}
            <p className="text-xs text-slate-400 text-center mb-4">
              Step {stepIndex + 1} of {TOUR_STEPS.length}
            </p>

            {/* Navigation buttons */}
            <div className="flex gap-2">
              {stepIndex > 0 ? (
                <Button 
                  variant="outline" 
                  onClick={handlePrev}
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              ) : (
                <Button 
                  variant="ghost" 
                  onClick={handleComplete}
                  className="flex-1 text-slate-400 hover:text-slate-300"
                  data-testid="skip-tour-btn"
                >
                  Skip Tour
                </Button>
              )}
              
              <Button 
                onClick={handleNext}
                className={`flex-1 ${
                  currentStep.type === 'outro' 
                    ? 'bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white' 
                    : currentStep.type === 'free' || currentStep.type === 'free-section'
                      ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white'
                      : currentStep.type === 'paid' || currentStep.type === 'paid-section'
                        ? currentStep.tier === 'premium'
                          ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white'
                          : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white'
                        : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white'
                }`}
                data-testid="tour-next-btn"
              >
                {stepIndex < TOUR_STEPS.length - 1 ? (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-1" />
                    Start Creating!
                  </>
                )}
              </Button>
            </div>
            
            {/* Always visible skip link */}
            {stepIndex > 0 && stepIndex < TOUR_STEPS.length - 1 && (
              <button
                onClick={handleComplete}
                className="w-full mt-3 text-xs text-slate-500 hover:text-slate-400"
                data-testid="skip-tour-link"
              >
                Skip Tour
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidedTour;
